// @djsem/shared — Brand World snapshot, Stem Intelligence refs, Show Pack / Release
// manifests, and the observability trace envelope.
// Stems: OFFLINE metadata only (rule 4 — no separation in live runtime, no GPU worker
// assumptions). Rights fields exist so restricted material is BLOCKED/warned at export
// (rule 10 — we never assume model or content rights).
import {
  vArray, vBoolean, vId, vIsoDate, vNumber, vObject, vRecord, vString, vUnit,
  type Validator,
} from "./validation";

// ---- Brand World snapshot ------------------------------------------------------

/** A snapshotted color palette (id, label, hex colors) captured from a Brand World. */
export interface BrandPaletteSnapshot { id: string; label: string; colors: string[] }
/** An immutable point-in-time capture of a Brand World's palette, motion language, and symbols. */
export interface BrandWorldSnapshot {
  id: string;
  brandWorldId: string;
  capturedAtIso: string;
  palette: BrandPaletteSnapshot;
  motionLanguage: string[];
  symbolSetIds: string[];
  typographyNote?: string;
  rulesNote?: string;
}
/** Runtime validator for {@link BrandWorldSnapshot}. */
export const vBrandWorldSnapshot: Validator<BrandWorldSnapshot> = vObject(
  {
    id: vId(), brandWorldId: vId(), capturedAtIso: vIsoDate(),
    palette: vObject({ id: vId(), label: vString({ min: 1 }), colors: vArray(vString({ pattern: /^#[0-9a-fA-F]{6}$/ }), { min: 1 }) }),
    motionLanguage: vArray(vString({ min: 1 })), symbolSetIds: vArray(vId()),
  },
  { typographyNote: vString({}), rulesNote: vString({}) },
) as Validator<BrandWorldSnapshot>;

// ---- Stem Intelligence (offline metadata only) -----------------------------------

/** The instrumental/vocal category of a separated stem. */
export type StemType = "drums" | "bass" | "vocals" | "melody" | "other";
/** Coarse rights status used by world-pack fixtures (see {@link DJSEMRightsState} for the canonical ledger vocabulary). */
export type RightsStatus = "unknown" | "owned" | "licensed" | "restricted";
/** Offline metadata describing a single separated stem (no audio, no live separation). */
export interface StemDescriptor {
  stemType: StemType;
  available: boolean;
  storageRef?: string;
  energyCurveRef?: string;
  confidence: number;
  rightsStatus: RightsStatus;
}
/** Lifecycle status of a stem-separation job. */
export type StemJobStatus = "not_requested" | "queued" | "processing" | "completed" | "failed" | "external_only";
/** A track's stem-separation job status and resulting stem descriptors (offline metadata only). */
export interface StemIntelligenceRef {
  id: string;
  preparationTrackId: string;
  status: StemJobStatus;
  source: "manual" | "external_runner" | "demucs" | "imported" | "unknown";
  stems: StemDescriptor[];
  warnings: string[];
}
const vRights = vString({ enum: ["unknown", "owned", "licensed", "restricted"] });
/** Runtime validator for {@link StemDescriptor}. */
export const vStemDescriptor: Validator<StemDescriptor> = vObject(
  { stemType: vString({ enum: ["drums", "bass", "vocals", "melody", "other"] }), available: vBoolean, confidence: vUnit(), rightsStatus: vRights },
  { storageRef: vString({ min: 1 }), energyCurveRef: vString({ min: 1 }) },
) as Validator<StemDescriptor>;
/** Runtime validator for {@link StemIntelligenceRef}. */
export const vStemIntelligenceRef: Validator<StemIntelligenceRef> = vObject(
  {
    id: vId(), preparationTrackId: vId(),
    status: vString({ enum: ["not_requested", "queued", "processing", "completed", "failed", "external_only"] }),
    source: vString({ enum: ["manual", "external_runner", "demucs", "imported", "unknown"] }),
    stems: vArray(vStemDescriptor), warnings: vArray(vString({})),
  },
) as Validator<StemIntelligenceRef>;

// ---- Show Pack manifest ------------------------------------------------------------

/** The kind of asset referenced by a world-pack show pack manifest. */
export type DJSEMShowPackAssetType =
  | "audio_reference" | "analysis_package" | "approved_snapshot" | "visual_scene"
  | "lighting_scene" | "brand_palette" | "stem_metadata" | "rendered_video" | "documentation";
/** A single asset entry within a world-pack show pack manifest. */
export interface DJSEMShowPackAsset {
  id: string;
  assetType: DJSEMShowPackAssetType;
  path: string;
  hash: string;
  required: boolean;
  rightsStatus: RightsStatus;
}
/** Overall rights summary for a show pack or release export manifest. */
export interface DJSEMShowPackRights { overall: RightsStatus; warnings: string[] }
/** Minimum schema/taxonomy versions a show pack requires to be readable. */
export interface DJSEMShowPackCompatibility { minSchemaVersion: string; taxonomyVersion: string }
/** The result of validating a show pack manifest at build time. */
export interface DJSEMShowPackValidation { validatedAtIso: string; valid: boolean; issues: string[] }
/**
 * A world-pack show pack manifest: assets, rights summary, schema compatibility, and
 * validation status.
 *
 * Renamed from `DJSEMShowPackManifest` (QA drift fix): the canonical packaging manifest
 * keeping that name is 12D `analysis-contract/showPackPackaging.DJSEMShowPackManifest`.
 * This older world-pack shape (createdAtIso, coarse rights) is kept for the v0.9
 * world-pack fixtures only.
 */
export interface DJSEMWorldShowPackManifest {
  id: string;
  version: string;
  title: string;
  description?: string;
  creatorId?: string;
  createdAtIso: string;
  assets: DJSEMShowPackAsset[];
  rights: DJSEMShowPackRights;
  compatibility: DJSEMShowPackCompatibility;
  validation: DJSEMShowPackValidation;
  approvedSnapshotHash?: string;
  simulationOnly: true; // packs never claim hardware output (rules 3/8)
}
/** Runtime validator for {@link DJSEMShowPackAsset}. */
export const vShowPackAsset: Validator<DJSEMShowPackAsset> = vObject(
  {
    id: vId(),
    assetType: vString({ enum: ["audio_reference", "analysis_package", "approved_snapshot", "visual_scene", "lighting_scene", "brand_palette", "stem_metadata", "rendered_video", "documentation"] }),
    path: vString({ min: 1 }), hash: vString({ min: 8 }), required: vBoolean, rightsStatus: vRights,
  },
) as Validator<DJSEMShowPackAsset>;
/** Runtime validator for {@link DJSEMWorldShowPackManifest}; enforces `simulationOnly: true`. */
export const vShowPackManifest: Validator<DJSEMWorldShowPackManifest> = vObject(
  {
    id: vId(), version: vString({ min: 1 }), title: vString({ min: 1 }), createdAtIso: vIsoDate(),
    assets: vArray(vShowPackAsset, { min: 1 }),
    rights: vObject({ overall: vRights, warnings: vArray(vString({})) }),
    compatibility: vObject({ minSchemaVersion: vString({ min: 1 }), taxonomyVersion: vString({ min: 1 }) }),
    validation: vObject({ validatedAtIso: vIsoDate(), valid: vBoolean, issues: vArray(vString({})) }),
    simulationOnly: (v, path = "$") => (v === true ? { valid: true, issues: [] } : { valid: false, issues: [{ path, message: "show packs must declare simulationOnly: true" }] }),
  },
  { description: vString({}), creatorId: vId(), approvedSnapshotHash: vString({ min: 8 }) },
) as Validator<DJSEMWorldShowPackManifest>;

// ---- Release / export manifest -------------------------------------------------------

/** The kind of release/export being packaged. */
export type DJSEMReleaseKind = "online_render" | "show_pack" | "asset_bundle";
/** A single asset entry within a release export manifest. */
export interface DJSEMReleaseItem { assetId: string; path: string; hash: string; bytes?: number }
/** A manifest describing the assets and rights summary of a release/export bundle. */
export interface DJSEMReleaseExportManifest {
  id: string;
  version: string;
  title: string;
  kind: DJSEMReleaseKind;
  createdAtIso: string;
  items: DJSEMReleaseItem[];
  rightsSummary: DJSEMShowPackRights;
  approvedSnapshotHash?: string;
}
/** Runtime validator for {@link DJSEMReleaseExportManifest}. */
export const vReleaseExportManifest: Validator<DJSEMReleaseExportManifest> = vObject(
  {
    id: vId(), version: vString({ min: 1 }), title: vString({ min: 1 }),
    kind: vString({ enum: ["online_render", "show_pack", "asset_bundle"] }), createdAtIso: vIsoDate(),
    items: vArray(vObject({ assetId: vId(), path: vString({ min: 1 }), hash: vString({ min: 8 }) }, { bytes: vNumber({ min: 0 }) }), { min: 1 }),
    rightsSummary: vObject({ overall: vRights, warnings: vArray(vString({})) }),
  },
  { approvedSnapshotHash: vString({ min: 8 }) },
) as Validator<DJSEMReleaseExportManifest>;

// ---- Observability trace envelope ----------------------------------------------------

/** Severity level of an observability trace event. */
export type DJSEMTraceSeverity = "debug" | "info" | "warning" | "error";
/** A single observability trace event envelope, correlatable via `traceId`/`spanId`. */
export interface DJSEMTraceEnvelope {
  traceId: string;
  spanId?: string;
  parentSpanId?: string;
  timeIso: string;
  timeMs: number;
  module: string;   // e.g. "prep_workspace", "live_conductor", "analysis_worker"
  event: string;    // machine-readable event name
  severity: DJSEMTraceSeverity;
  packageId?: string;
  trackId?: string;
  data?: Record<string, unknown>;
}
/** Runtime validator for {@link DJSEMTraceEnvelope}. */
export const vTraceEnvelope: Validator<DJSEMTraceEnvelope> = vObject(
  {
    traceId: vId(), timeIso: vIsoDate(), timeMs: vNumber({ min: 0 }),
    module: vString({ min: 1 }), event: vString({ min: 1 }),
    severity: vString({ enum: ["debug", "info", "warning", "error"] }),
  },
  { spanId: vId(), parentSpanId: vId(), packageId: vId(), trackId: vId(), data: vRecord((v) => ({ valid: true, issues: [] })) },
) as Validator<DJSEMTraceEnvelope>;
